import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import queryString from "query-string";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes, setFontVariant, colorsSets } from "@similarweb/styles";
import { LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";

import { MetricContainer } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { swSettings } from "app/common/services/swSettings";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { Legends } from "app/components/React/Legends/Legends";
import type { ILegendItem, ILegendItems } from "app/components/React/Legends/legendsTypes";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { deviceTypeToTextMap } from "app/pages/website-analysis/traffic-sources/search/constants/paidSearchGraphConstants";
import {
    LegendsContainer,
    StyledLegendWrapper,
} from "app/pages/website-analysis/traffic-sources/search/constants/paidSearchGraphStyledComponents";
import { pngDownload } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import DurationService, { apiFormat, BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { FlexRow, RightFlexRow, FlexColumn, BaseFlex } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { ValueOf } from "pro-features/types";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { useMonitorKeywordsEndpoints } from "../../monitorKeywordsEndpoints";

import { getChartConfig } from "./VolumeOverTimeGraphConfig";
const colorSet = colorsSets.b1.toArray();
const fetchService = DefaultFetchService.getInstance();

const getPreviousMonthDate = (dateStr: string): string => {
    return dayjs(dateStr, apiFormat).subtract(1, "month").format(apiFormat);
};

const GraphHeaderActions = styled(RightFlexRow)`
    flex: 1;
`;

const VolumeOverTimeGraphLegendsContainer = styled(FlexColumn)`
    padding: 24px 0 24px 14px;
`;

const VolumeOverTimeGraphLegendsHeader = styled(BaseFlex)`
    border: none;
    justify-content: space-between;
    padding-bottom: 6px;
`;

const TableHeaderText = styled.span`
    ${setFontVariant("captionTitle", colorsPalettes.carbon[400])}
`;

const BoxTitleContainer = styled(FlexRow)`
    position: relative;
    height: 64px;
    align-items: center;
    ${setFontVariant("h5", colorsPalettes.carbon[500])}

    &::after {
        content: "";
        position: absolute;
        border-bottom: 1px solid ${colorsPalettes.carbon[100]};
        bottom: 0;
        /* bleed out of padding */
        width: calc(100% + 48px);
        margin: 0 -24px;
    }
`;

const MetricContainerStyled = styled(MetricContainer)`
    box-sizing: border-box;
    margin-bottom: 24px;
    padding-top: 0;
    width: 100%;
    height: 310px;
`;

type IVolumeResponse = {
    Data: {
        [key: string]: {
            VolumesMonthly: Record<string, number>[];
            MobileShareMonthly: Record<string, number>[];
            MobileShare: number;
            AvgVolume: number;
        };
    };
};

interface IGraphData<T> extends Record<ValueOf<typeof DEVICES_TYPES_FOR_API>, T> {
    [DEVICES_TYPES_FOR_API.MOBILE]: T;
    [DEVICES_TYPES_FOR_API.DESKTOP]: T;
    [DEVICES_TYPES_FOR_API.TOTAL]: T;
}

interface ITrendSeries {
    data: any[];
    name: ValueOf<typeof DEVICES_TYPES_FOR_API>;
    color: string;
    marker: Record<string, string>;
    visible: boolean;
}

const defaultGraphTrends: IGraphData<ITrendSeries> = {
    [DEVICES_TYPES_FOR_API.MOBILE]: {
        data: [],
        name: DEVICES_TYPES_FOR_API.MOBILE,
        color: colorSet[0],
        marker: { symbol: "circle" },
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.DESKTOP]: {
        data: [],
        name: DEVICES_TYPES_FOR_API.DESKTOP,
        color: colorSet[1],
        marker: { symbol: "circle" },
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.TOTAL]: {
        data: [],
        name: DEVICES_TYPES_FOR_API.TOTAL,
        color: colorsPalettes.midnight[50],
        marker: { symbol: "circle" },
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.COMBINED]: {
        data: [],
        name: DEVICES_TYPES_FOR_API.COMBINED,
        color: colorSet[0],
        marker: { symbol: "circle" },
        visible: true,
    },
};

interface IGraphAverage {
    label: string;
    value: number;
    color: string;
    visible: boolean;
}

const defaultGraphAverages: IGraphData<IGraphAverage> = {
    [DEVICES_TYPES_FOR_API.MOBILE]: {
        label: deviceTypeToTextMap.MobileWeb,
        value: 0,
        color: colorSet[0],
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.DESKTOP]: {
        label: deviceTypeToTextMap.Desktop,
        value: 0,
        color: colorSet[1],
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.TOTAL]: {
        label: deviceTypeToTextMap.Total,
        value: 0,
        color: colorsPalettes.midnight[50],
        visible: true,
    },
    [DEVICES_TYPES_FOR_API.COMBINED]: {
        label: deviceTypeToTextMap.Total,
        value: 0,
        color: colorsPalettes.midnight[50],
        visible: true,
    },
};

interface VolumeOverTimeGraphProps {
    keywordGroup: IUserKeywordGroup;
    displayHeader: boolean;
    containerComponent?: typeof MetricContainerStyled;
}

export const VolumeOverTimeGraph: FC<VolumeOverTimeGraphProps> = ({
    keywordGroup,
    displayHeader,
    containerComponent,
}) => {
    const { params } = useCurrentStateAndParams();
    const endpoints = useMonitorKeywordsEndpoints();
    const graphContainerRef = useRef<HTMLDivElement>(null);
    const [graphTrends, setGraphTrends] = useState(defaultGraphTrends);
    const [graphAverages, setGraphAverages] = useState(defaultGraphAverages);
    const downloadExcelPermitted = swSettings.current.resources.IsExcelAllowed;
    const ContainerComponent = containerComponent ?? MetricContainerStyled;

    const getQueryParams = () => {
        const { country, isWWW, duration, webSource } = params;
        const durationObject = DurationService.getDurationData(duration);
        const { from, to, isWindow } = durationObject.forAPI;
        const userHasMoreThan12MonthsAvailable = swSettings?.current?.resources?.SnapshotInterval?.count > 12;
        //take 13 months only in case the user can have 12m.
        const finalFromDate =
            userHasMoreThan12MonthsAvailable && duration === BasicDurations.LAST_TWELVE_MONTHS
                ? getPreviousMonthDate(from)
                : from; //we want to display the previous month data as start date.

        return {
            country,
            from: finalFromDate,
            includeSubDomains: isWWW === "*",
            isWindow,
            keys: keywordGroup.Id,
            to,
            webSource,
            GroupHash: keywordGroup.GroupHash,
            UseVolume2: true,
        };
    };

    const transformVolumeData = (volumeResponse: IVolumeResponse, queryParams: ReturnType<typeof getQueryParams>) => {
        const { VolumesMonthly, MobileShareMonthly, MobileShare, AvgVolume } = volumeResponse.Data[queryParams.keys];

        const trends = VolumesMonthly.reduce((agr, item, idx) => {
            const [dateString, value] = Object.entries(item)[0];
            const mobileShareValue = MobileShareMonthly[idx][dateString];
            const date = dayjs(dateString).valueOf();
            agr.Mobile.data.push([date, value * mobileShareValue]);
            agr.Desktop.data.push([date, value * (1 - mobileShareValue)]);
            agr.Total.data.push([date, value]);
            return agr;
        }, cloneDeep(defaultGraphTrends));

        const averages = {
            Mobile: { ...defaultGraphAverages.Mobile, value: AvgVolume * MobileShare },
            Desktop: { ...defaultGraphAverages.Desktop, value: AvgVolume * (1 - MobileShare) },
            Total: { ...defaultGraphAverages.Total, value: AvgVolume },
        };

        return { trends, averages };
    };

    const { data, isLoading, error, isError } = useQuery({
        queryKey: ["volumeOverTime", keywordGroup.GroupHash, params],
        queryFn: async () => {
            const queryParams = getQueryParams();
            const response = await fetchService.get<IVolumeResponse>(endpoints.volumeOverTimeGraph, queryParams);
            return transformVolumeData(response, queryParams);
        },
        retry: 3,
    });

    const getExcelUrl = () => {
        const queryStringParams = queryString.stringify(getQueryParams());
        return `${endpoints.volumeOverTimeExcel}?${queryStringParams}`;
    };

    const onTrendToggle = (item: ILegendItem & { id: string }) => {
        const setterFunction = (state: IGraphData<any>) => ({
            ...state,
            [item.id]: { ...state[item.id], visible: !state[item.id].visible },
        });
        setGraphTrends(setterFunction);
        setGraphAverages(setterFunction);
    };

    const handlePngDownload = () => {
        pngDownload(graphContainerRef.current, translate("keywordanalysis.organic2.clicks.over.time.title.tooltip"));
    };

    const handlePNGTracking = () => {
        allTrackers.dispatchTrackEvent(
            "keywords.mobile.2.total.search.excel.download",
            "submit-ok",
            "keywords.mobile.2.total.search.excel.download",
        );
    };

    const handleExcelTracking = () => {
        allTrackers.dispatchTrackEvent(
            "keywords.mobile.2.total.search.excel.download",
            "submit-ok",
            "keywords.mobile.2.total.search.excel.download",
        );
    };

    const legendsItem: ILegendItems = Object.entries(graphAverages).map(([name, record]) => ({
        name: record.label,
        id: name,
        data: abbrNumberFilter()(record.value),
        color: record.color,
        isHidden: false,
        visible: record.visible ?? true,
    }));

    useEffect(() => {
        if (data) {
            setGraphTrends(data.trends);
            setGraphAverages(data.averages as IGraphData<IGraphAverage>);
        }
    }, [data]);

    useEffect(() => {
        if (isError && error) {
            Sentry.error("Error while fetching volume over time graph data", error);
        }
    }, [isError, error]);

    return (
        <div ref={graphContainerRef}>
            <ContainerComponent>
                {displayHeader ? (
                    <BoxTitle
                        Container={BoxTitleContainer}
                        customElement={
                            <GraphHeaderActions>
                                <DownloadButtonMenu
                                    Excel={true}
                                    downloadUrl={getExcelUrl()}
                                    excelLocked={!downloadExcelPermitted}
                                    trackingFunction={handleExcelTracking}
                                />
                                <DownloadButtonMenu
                                    PNG={true}
                                    handleDownloadFunction={handlePngDownload}
                                    trackingFunction={handlePNGTracking}
                                />
                            </GraphHeaderActions>
                        }
                    >
                        {translate("keywordresearch.monitorkeywords.over.time.graph.title")}
                    </BoxTitle>
                ) : null}
                {isLoading ? (
                    <FlexRow justifyContent="space-between">
                        <GraphLoader
                            width="100%"
                            height="228"
                        />
                    </FlexRow>
                ) : (
                    <FlexRow justifyContent="space-between">
                        <Chart
                            type="line"
                            config={getChartConfig(graphTrends)}
                            data={Object.values(graphTrends)}
                            domProps={{
                                style: {
                                    height: "100%",
                                    width: "100%",
                                },
                            }}
                        />
                        <VolumeOverTimeGraphLegendsContainer>
                            <VolumeOverTimeGraphLegendsHeader>
                                <TableHeaderText>
                                    {translate("keywordresearch.monitorkeywords.over.time.graph.tooltip.device")}
                                </TableHeaderText>
                                <TableHeaderText>
                                    {translate(
                                        "keywordresearch.monitorkeywords.over.time.graph.legends.avg.monthly.volume",
                                    )}
                                </TableHeaderText>
                            </VolumeOverTimeGraphLegendsHeader>
                            <LegendsContainer className="separatorLine">
                                <Legends
                                    legendItems={legendsItem}
                                    toggleSeries={onTrendToggle}
                                    legendComponent={LegendWithOneLineCheckboxFlex}
                                    legendComponentWrapper={StyledLegendWrapper}
                                    textMaxWidth={"128px"}
                                />
                            </LegendsContainer>
                        </VolumeOverTimeGraphLegendsContainer>
                    </FlexRow>
                )}
            </ContainerComponent>
        </div>
    );
};
